import React from 'react';
import type { Note } from '@/context/NotesContext';

interface NoteCardProps {
  note: Note;
  onEdit: (note: Note) => void;
  onToggleInjection: (noteId: string) => void;
}

const NoteCard: React.FC<NoteCardProps> = ({ note, onEdit }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'just now';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getPreview = (content: string) => {
    return content.length > 100 ? content.substring(0, 100) + '...' : content;
  };

  return (
    <div
      className={`bg-card dark:bg-card-dark border border-border rounded-lg p-4 mb-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-[hsl(var(--primary))] hover:-translate-y-0.5 relative group ${note.isInjected ? 'border-green-500 bg-success/5' : ''}`}
      onClick={() => onEdit(note)}
    >
      {/* Header with tool name, date, and status */}
      <div className='flex items-center justify-between mb-2'>
        <h3 className='text-sm font-semibold line-clamp-1 flex-1'>
          {note.toolName}
        </h3>
        <div className='flex items-center gap-3 ml-2'>
          <span className='text-xs text-foreground dark:text-foreground-dark'>
            {formatDate(note.createdAt)}
          </span>
          <div
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${note.isActive ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}
          >
            <div
              className={`w-1.5 h-1.5 rounded-full ${note.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`}
            />
            {note.isActive ? 'Injected' : 'Not Injected'}
          </div>
        </div>
      </div>

      {/* Description */}
      <div className='text-xs text-foreground dark:text-foreground-dark line-clamp-1 leading-relaxed mb-2'>
        {note.toolDescription || 'No description'}
      </div>

      {/* Preview Content - Tool Body */}
      <div className='text-xs text-foreground dark:text-foreground-dark line-clamp-2 leading-relaxed bg-muted/30 rounded p-2 font-mono'>
        {getPreview(note.toolBody)}
      </div>
    </div>
  );
};

export default NoteCard;
